import React, { Fragment, Suspense, useEffect } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { SmartCardProvider } from '@atlaskit/link-provider';
import { getFrontendExperiment } from '@atlassian/search-experiment';

import { messages } from '../../messages';
import { useAIAnswerContext } from '../ai-answer-context';
import { AIAnswerContentWarningAndBrandSection } from '../ai-answer-footer/ai-answer-footer';
import { onAIAnswerLoadingDialogShown } from '../analytics';
import { Collapse } from '../smart-answer-card/collapse';
import { NLPSearchResultFormat } from '../types';

import { AIAnswerLoadingStream } from './ai-answer-loading-stream';
import GeneratingAnimation from './generating-animation';
import { AIAnswerSearchLoadingBorder, GeneratingContainer, GeneratingText } from './styled';

type AIAnswerLoadingDialogProps = {
	collapsed?: boolean;
	toggleCollapsedState?: () => void;
	callbacks?: {
		// called when card is rendered in the expanded state due to the answer being short
		onExpandedByDefault?: () => void;
		onExpandClicked?: () => void;
		onCollapseClicked?: () => void;
	};
};

// Used in quicksearch/advanced search
export const AIAnswerLoadingDialog = ({
	collapsed,
	toggleCollapsedState,
	callbacks,
}: AIAnswerLoadingDialogProps) => {
	const { commonAttributes, fireAnalyticsEvent } = useAIAnswerContext();

	useEffect(() => {
		fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<LegacySmartAnswerLoadingDialog
			collapsed={collapsed}
			toggleCollapsedState={toggleCollapsedState}
			callbacks={callbacks}
		/>
	);
};

const LegacySmartAnswerLoadingDialog = ({
	collapsed = true,
	toggleCollapsedState,
	callbacks,
}: AIAnswerLoadingDialogProps) => {
	const { partialResponse, showBookends, queryReasoning } = useAIAnswerContext();
	const { formatMessage } = useIntl();

	const { serveExperiment: serveSAINReasoningExperiment } = getFrontendExperiment({
		abTestId: 'kd_sain_reasoning_exp',
	});

	if (!partialResponse || partialResponse?.format !== NLPSearchResultFormat.MARKDOWN) {
		return (
			<AIAnswerSearchLoadingBorder isStreaming={false}>
				<GeneratingContainer>
					<GeneratingAnimation alt={formatMessage(messages.ai_answer_generating_icon_alt_text)} />
					<GeneratingText>
						{serveSAINReasoningExperiment && queryReasoning ? (
							queryReasoning
						) : (
							<FormattedMessage {...messages.ai_answer_generating_rovo} />
						)}
					</GeneratingText>
				</GeneratingContainer>
				{!showBookends ? <AIAnswerContentWarningAndBrandSection /> : null}
			</AIAnswerSearchLoadingBorder>
		);
	}

	return (
		<AIAnswerSearchLoadingBorder isStreaming>
			<Suspense fallback={<Fragment />}>
				<SmartCardProvider>
					<Collapse
						collapsed={collapsed}
						isLoading
						isStreaming
						expandWhenClickedAnywhere
						toggleCollapsedState={toggleCollapsedState}
						callbacks={callbacks}
					>
						<AIAnswerLoadingStream markdown={partialResponse.partialNlpResult} />
					</Collapse>
					{!showBookends ? <AIAnswerContentWarningAndBrandSection /> : null}
				</SmartCardProvider>
			</Suspense>
		</AIAnswerSearchLoadingBorder>
	);
};
